// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { BLEND_STATUS, CROP_STATUS, type WeightUnit, dayOfWeekToNumber, utils } from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import { getNumericID } from '../../utils';
import {
  EVERY_NTH_WEEKDAY_OF_MONTH,
  EVERY_X_DAY_OF_WEEK,
  type FrequencyLabel,
  frequencyLabelToInput,
} from '../common/frequency';
import { sortCropsByName } from '../crop/sortCropsByName';
import type { CropOrBlendToUseFormInput } from './NewCropOrBlendToUseForm';
import type { Props as NewCropOrBlendToUseFormProps } from './NewCropOrBlendToUseForm';
import { getBestSizeKey, getSizeParamsFromKey } from './sizeUtils';

interface CropData {
  readonly edges: Array<{
    readonly node: {
      readonly id: string;
      readonly crop_name: string;
      readonly status: string;
      readonly total_grow_time: number;
      readonly harvest_buffer_time: number;
      readonly yield_per_tray: string;
      readonly yield_unit: string;
      readonly buffer_percentage: number;
      readonly edge_sizes: {
        readonly edges: Array<{
          readonly node: {
            readonly id: string;
            readonly size: string;
            readonly unit: string;
            readonly price_per_item?: string | null;
          };
        }>;
      };
    };
  }>;
}

interface BlendData {
  readonly edges: Array<{
    readonly node: {
      readonly id: string;
      readonly blend_name: string;
      readonly status: string;
      readonly edge_sizes: {
        readonly edges: Array<{
          readonly node: {
            readonly id: string;
            readonly size: string;
            readonly unit: string;
            readonly price_per_item?: string | null;
          };
        }>;
      };
      readonly crops: {
        readonly edges: Array<{
          readonly node: {
            readonly id: string;
            readonly percent_of_blend: number;
            readonly crop?: {
              readonly id: string;
              readonly crop_name: string;
              readonly total_grow_time: number;
              readonly harvest_buffer_time: number;
              readonly yield_per_tray: string;
              readonly yield_unit: string;
              readonly buffer_percentage: number;
            } | null;
          };
        }>;
      };
    };
  }>;
}

export function filterParseSortCropData(cropsData: CropData) {
  const sortedCropData = cropsData.edges
    .map(({ node }) => node)
    .filter((crop) => crop.status !== CROP_STATUS.ARCHIVED)
    .map((crop) => ({
      ...crop,
      sizes: crop.edge_sizes.edges.map(({ node }) => ({
        ...node,
        price_per_item: node.price_per_item != null ? Number.parseFloat(node.price_per_item) : undefined,
        product_id: getNumericID(node.id),
        size: Number.parseFloat(node.size),
      })),
      type: 'crop' as const,
    }));

  return sortCropsByName(sortedCropData);
}
export function filterParseSortBlendData(blendsData: BlendData) {
  return blendsData.edges
    .map(({ node }) => node)
    .filter((blend) => blend.status !== BLEND_STATUS.ARCHIVED)
    .map((blend) => ({
      ...blend,
      sizes: blend.edge_sizes.edges.map(({ node }) => ({
        ...node,
        price_per_item: node.price_per_item != null ? Number.parseFloat(node.price_per_item) : undefined,
        product_id: getNumericID(node.id),
        size: Number.parseFloat(node.size),
      })),
      type: 'blend' as const,
    }));
}

interface FrequencyOutput {
  readonly start_date: number;
  readonly end_date: number | null;
  readonly options: {
    readonly exceptions: string[];
    readonly frequency_groups: {
      readonly frequency_type: string;
      readonly frequencies: number[];
    }[];
  };
}

export function prepareFrequencyForSubmit({
  startDate,
  timezone,
  endDate,
  frequencyUnit,
  dayOfWeek,
  frequency,
  recursForever,
  occurence,
}: {
  readonly startDate: dayjs.Dayjs | null;
  readonly endDate: dayjs.Dayjs | null;
  readonly timezone: string;
  readonly frequencyUnit: string;
  readonly dayOfWeek: string;
  readonly frequency: string;
  readonly recursForever: boolean;
  readonly occurence: string;
}): FrequencyOutput {
  return {
    start_date: utils.toNoonTime(startDate ?? utils.todayStart(), timezone).unix(),
    end_date: recursForever ? null : endDate == null ? null : utils.toNoonTime(endDate, timezone).unix(),
    options: {
      exceptions: [],
      frequency_groups: [
        {
          frequency_type: frequencyLabelToInput(frequencyUnit as FrequencyLabel),
          frequencies:
            frequencyUnit === EVERY_NTH_WEEKDAY_OF_MONTH
              ? [dayOfWeekToNumber(dayOfWeek || 'mon'), Number.parseInt(occurence || '0')]
              : [
                  frequencyUnit === EVERY_X_DAY_OF_WEEK
                    ? dayOfWeekToNumber(dayOfWeek || 'mon')
                    : Number.parseInt(frequency || '0'),
                ],
        },
      ],
    },
  };
}

interface CreateOrderUseInputBase {
  readonly count: number;
  readonly size: number | null;
  readonly unit: string;
  readonly planting_time: null;
  readonly price_per_item: number | null;
  readonly product_id: number | null;
}

interface CreateOrderUseBlendsInput extends CreateOrderUseInputBase {
  readonly blend_id: number;
}
interface CreateOrderUseCropsInput extends CreateOrderUseInputBase {
  readonly crop_id: number;
}

function parseCropOrBlendFormValues(values: CropOrBlendToUseFormInput): CreateOrderUseInputBase {
  const { itemCount, countUnit, pricePerItem } = values;
  const { size, unit, productId } = getSizeParamsFromKey(countUnit);

  return {
    count: Number.parseInt(itemCount),
    size: size ?? null,
    unit,
    planting_time: null,
    price_per_item: pricePerItem != null ? Number.parseFloat(pricePerItem) : null,
    product_id: productId ?? null,
  };
}

export function preprareFormsForSubmit(cropOrBlendForms: readonly React.ReactElement<NewCropOrBlendToUseFormProps>[]): {
  readonly blendsToUse: CreateOrderUseBlendsInput[];
  readonly cropsToUse: CreateOrderUseCropsInput[];
} {
  const cropsToUse = cropOrBlendForms
    .filter(
      (form) => form.props.values.cropOrBlendSelected != null && form.props.values.cropOrBlendSelected.type === 'crop',
    )
    .map((form) => {
      const { cropOrBlendSelected } = form.props.values;

      return {
        // Hacky way to make sure error comes from server instead of
        // crashing UI just in case cropSelected is somehow undefined
        crop_id: cropOrBlendSelected ? getNumericID(cropOrBlendSelected.id) : -1,
        ...parseCropOrBlendFormValues(form.props.values),
      };
    });
  const blendsToUse = cropOrBlendForms
    .filter(
      (form) => form.props.values.cropOrBlendSelected != null && form.props.values.cropOrBlendSelected.type === 'blend',
    )
    .map((form) => {
      const { cropOrBlendSelected } = form.props.values;

      return {
        // Hacky way to make sure error comes from server instead of
        // crashing UI just in case cropSelected is somehow undefined
        blend_id: cropOrBlendSelected ? getNumericID(cropOrBlendSelected.id) : -1,
        ...parseCropOrBlendFormValues(form.props.values),
      };
    });

  return {
    blendsToUse,
    cropsToUse,
  };
}

type InputType = {
  readonly id: string;
  readonly size_amount?: string | null;
  readonly quantity: string;
  readonly unit: string;
  readonly price_per_item?: string | null;
  readonly product_id?: string | null;
  readonly crop?: {
    readonly id: string;
    readonly crop_name: string;
    readonly status: string;
    readonly total_grow_time: number;
    readonly harvest_buffer_time: number;
    readonly yield_per_tray: string;
    readonly yield_unit: string;
    readonly buffer_percentage: number;
    readonly edge_sizes: {
      readonly edges: Array<{
        readonly node: {
          readonly id: string;
          readonly size: string;
          readonly unit: string;
          readonly price_per_item?: string | null;
        };
      }>;
    };
  } | null;
  readonly blend?: {
    readonly id: string;
    readonly blend_name: string;
    readonly status: string;
    readonly edge_sizes: {
      readonly edges: Array<{
        readonly node: {
          readonly id: string;
          readonly size: string;
          readonly unit: string;
          readonly price_per_item?: string | null;
        };
      }>;
    };
    readonly crops: {
      readonly edges: Array<{
        readonly node: {
          readonly id: string;
          readonly percent_of_blend: number;
          readonly crop?: {
            readonly id: string;
            readonly total_grow_time: number;
            readonly harvest_buffer_time: number;
            readonly yield_per_tray: string;
            readonly yield_unit: string;
            readonly crop_name: string;
            readonly buffer_percentage: number;
          } | null;
        };
      }>;
    };
  } | null;
};

function parseOrderInputsForForms({
  inputs,
}: {
  readonly inputs: InputType[];
}): {
  readonly cropInputs: ParsedCropInput[];
  readonly blendInputs: ParsedBlendInput[];
} {
  const cropInputs: ParsedCropInput[] = inputs
    .map((input) => {
      if (input.crop == null) {
        return;
      }
      if (input.crop.status === CROP_STATUS.ARCHIVED) {
        return;
      }

      // This is so that if a product was deleted, or otherwise doesn't exist
      // when the cloned or edited input is parsed it's still available in
      // the size options dropdown

      // Note that this could cause the dropdown to have two of the same sizes
      // if the old product was deleted, and then created again with the same size
      const inputSize: Size | null =
        input.size_amount == null
          ? null
          : {
              unit: input.unit,
              price_per_item: undefined,
              product_id: undefined,
              size: Number.parseFloat(input.size_amount),
            };

      return {
        ...input,
        crop: {
          ...input.crop,
          sizes: input.crop.edge_sizes.edges
            .map<Size>(({ node }) => ({
              unit: node.unit,
              price_per_item: node.price_per_item != null ? Number.parseFloat(node.price_per_item) : undefined,
              product_id: getNumericID(node.id),
              size: Number.parseFloat(node.size),
            }))
            .concat(inputSize != null ? [inputSize] : []),
          type: 'crop' as const,
        },
      };
    })
    .filter(utils.notNull);
  const blendInputs: ParsedBlendInput[] = inputs
    .map((input) => {
      if (input.blend == null) {
        return;
      }
      if (input.blend.status === BLEND_STATUS.ARCHIVED) {
        return;
      }
      const inputSize: Size | null =
        input.size_amount == null
          ? null
          : {
              unit: input.unit,
              price_per_item: undefined,
              product_id: undefined,
              size: Number.parseFloat(input.size_amount),
            };

      return {
        ...input,
        blend: {
          ...input.blend,
          sizes: input.blend.edge_sizes.edges
            .map<Size>(({ node }) => ({
              unit: node.unit,
              price_per_item: node.price_per_item != null ? Number.parseFloat(node.price_per_item) : undefined,
              product_id: getNumericID(node.id),
              size: Number.parseFloat(node.size),
            }))
            .concat(inputSize != null ? [inputSize] : []),
          type: 'blend' as const,
        },
      };
    })
    .filter(utils.notNull);

  return {
    cropInputs,
    blendInputs,
  };
}

interface Size {
  readonly price_per_item: number | undefined;
  readonly product_id: number | undefined;
  readonly size: number;
  readonly unit: string;
}

interface ParsedInputBase {
  readonly id: string;
  readonly size_amount?: string | null;
  readonly quantity: string;
  readonly unit: string;
  readonly price_per_item?: string | null;
  readonly product_id?: string | null;
}

interface ParsedCropInput extends ParsedInputBase {
  readonly crop: {
    readonly type: 'crop';
    readonly id: string;
    readonly crop_name: string;
    readonly status: string;
    readonly total_grow_time: number;
    readonly harvest_buffer_time: number;
    readonly yield_per_tray: string;
    readonly yield_unit: string;
    readonly buffer_percentage: number;
    readonly sizes: readonly Size[];
  };
}

interface ParsedBlendInput extends ParsedInputBase {
  readonly blend: {
    readonly type: 'blend';
    readonly id: string;
    readonly blend_name: string;
    readonly status: string;
    readonly sizes: readonly Size[];
    readonly crops: {
      readonly edges: Array<{
        readonly node: {
          readonly id: string;
          readonly percent_of_blend: number;
          readonly crop?: {
            readonly id: string;
            readonly total_grow_time: number;
            readonly harvest_buffer_time: number;
            readonly yield_per_tray: string;
            readonly yield_unit: string;
            readonly crop_name: string;
            readonly buffer_percentage: number;
          } | null;
        };
      }>;
    };
  };
}

export function inputsToNewCropOrBlendToUseFormProps({
  orderInputs,
  preferredWeightUnit,
}: {
  readonly orderInputs: InputType[];
  readonly preferredWeightUnit: WeightUnit;
}): {
  readonly cropFormValues: readonly CropOrBlendToUseFormInput[];
  readonly blendFormValues: readonly CropOrBlendToUseFormInput[];
} {
  const { cropInputs, blendInputs } = parseOrderInputsForForms({
    inputs: orderInputs,
  });
  const cropForms = cropInputs.map((ci) => {
    const { bestSizeKey, bestPricePerItem } = getBestSizeKey({
      inputSize: ci.size_amount ? Number.parseFloat(ci.size_amount) : null,
      inputUnit: ci.unit,
      inputProductId: ci.product_id ? Number.parseInt(ci.product_id) : null,
      inputPricePerItem: ci.price_per_item ? Number.parseFloat(ci.price_per_item) : null,
      currentProducts: ci.crop.sizes.map((s) => ({
        size: s.size,
        unit: s.unit,
        pricePerItem: s.price_per_item ?? null,
        productId: s.product_id ?? null,
      })),
      preferredWeightUnit,
    });

    return {
      cropOrBlendSelected: ci.crop,
      itemCount: ci.quantity,
      harvestTime: dayjs(),
      itemCountValidation: '',
      pricePerItem: bestPricePerItem?.toString() ?? '',
      pricePerItemValidation: '',
      countUnit: bestSizeKey,
    };
  });

  const blendForms = blendInputs.map((bi) => {
    const { bestSizeKey, bestPricePerItem } = getBestSizeKey({
      inputSize: bi.size_amount ? Number.parseFloat(bi.size_amount) : null,
      inputUnit: bi.unit,
      inputProductId: bi.product_id ? Number.parseInt(bi.product_id) : null,
      inputPricePerItem: bi.price_per_item ? Number.parseFloat(bi.price_per_item) : null,
      currentProducts: bi.blend.sizes.map((s) => ({
        size: s.size,
        unit: s.unit,
        pricePerItem: s.price_per_item ?? null,
        productId: s.product_id ?? null,
      })),
      preferredWeightUnit,
    });

    return {
      cropOrBlendSelected: bi.blend,
      itemCount: bi.quantity,
      harvestTime: dayjs(),
      itemCountValidation: '',
      pricePerItem: bestPricePerItem?.toString() ?? '',
      pricePerItemValidation: '',
      countUnit: bestSizeKey,
    };
  });

  return {
    cropFormValues: cropForms,
    blendFormValues: blendForms,
  };
}
